"use client";

import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { CustomScroll } from "@/components/CustomScroll";
import { useTargomoKey } from "@/features/isochrone/TargomoKeyContext";
import { useIsMobile } from "@/features/layout/useIsMobile";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { StreetViewToggle } from "@/features/streetview/components/StreetViewToggle";
import { useStreetView } from "@/features/streetview/useStreetView";
import { cn } from "@/lib/cn";

/**
 * `mapbox-gl-draw` and the turf functions that measure its geometry are around
 * half a megabyte between them — more than the entire rest of the map page. Most
 * tenants don't enable the tool at all, and those that do only reach it after
 * opening this tab, so it is fetched then rather than shipped to everyone.
 */
const Measurement = dynamic(
  () =>
    import("@/features/measurement/components/Measurement").then(
      (module) => module.Measurement
    ),
  { ssr: false }
);

/** Same reasoning: the Targomo client is only needed once this tab is open. */
const Isochrone = dynamic(
  () =>
    import("@/features/isochrone/components/Isochrone").then(
      (module) => module.Isochrone
    ),
  { ssr: false }
);

/**
 * Which tools this tenant has, at this breakpoint.
 *
 * The tools draw on the map and read distances off it, so they are desktop-only:
 * on a phone the map is a separate view from the panel, and a tool whose output
 * you can't see while you use it isn't one.
 *
 * The isochrone additionally needs an API key. Without one every request to
 * Targomo fails, so the tool is not offered at all rather than offered and then
 * failing on the first click.
 *
 * Street view is the exception to the desktop-only rule: it is one switch over
 * map layers, which works as well from the mobile menu as from here.
 */
export function useEnabledTools() {
  const { showPolygonSwitch, showTargomoSwitch } = useProjectConfigs();
  const isMobile = useIsMobile();
  const { isAvailable: hasStreetView } = useStreetView();
  const targomoKey = useTargomoKey();

  return {
    streetView: hasStreetView,
    measurement: !isMobile && !!showPolygonSwitch,
    isochrone: !isMobile && !!showTargomoSwitch && !!targomoKey,
  };
}

/** Whether the tools tab is worth showing at all. */
export function useHasTools(): boolean {
  return Object.values(useEnabledTools()).some(Boolean);
}

/** The tools panel: one collapsible section per tool the tenant enabled. */
export function ToolsTab() {
  const t = useTranslations();
  const isMobile = useIsMobile();
  const tools = useEnabledTools();

  return (
    <CustomScroll className={cn("mr-2 mt-4", isMobile && "max-h-[60vh]")}>
      <div className="mx-5">
        <span
          className={cn(
            "flex py-4 font-semibold text-black dark:text-off-white",
            isMobile ? "text-base" : "justify-center text-sm"
          )}
        >
          {t("tools.title")}
        </span>

        <div className={cn("space-y-4", isMobile && "ml-2")}>
          {tools.streetView && <StreetViewToggle />}
          {tools.isochrone && <Isochrone />}
          {tools.measurement && <Measurement />}
        </div>
      </div>
    </CustomScroll>
  );
}
